/* global globalThis */
// Роллаут-выбор плана раунда: несколько кандидатов разной агрессивности
// прогоняются в песочнице (по образцу strongMode/simulateOpponentsWarPhases)
// до конца СЛЕДУЮЩЕГО раунда, играет план с лучшим прогнозом счета.
//
// Ключевое отличие от эвристического EV: альтернативная стоимость агрессии
// (потерянный доход/развитие за время марша, оголенная оборона) учитывается
// автоматически — план «сидеть и развиваться» соревнуется с планами атак в
// одной песочнице по одной метрике. Именно ее игнорирование сделало
// прямолинейные группы атак нетто-минусом (см. new_arch_plan.md, проба 4).
//
// Управление ботом в песочнице — через модульный кэш plannerStore: кандидат
// проставляется в кэш до клонирования, песочный decideMovementStep читает его
// как «уже построенный план раунда». Вложенных роллаутов нет: в песочнице
// план всегда берется из кэша (см. гейт __isBotSandbox в decideMovementStep).
import {createStore} from "redux";
import {rootReducer} from "../../../redux/reducers";
import {
  selectCurrentPlayer,
  selectGameResults,
  selectPlayers,
  selectRound,
  selectUnits,
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {executeBotStep} from "../executeBotStep";
import {handleCompleteTurn} from "../../gameState/turn/handleCompleteTurn";
import {restoreBotStrategies, snapshotBotStrategies} from "../strategy/botStrategyStore";
import {resetMeleeHandlingTracking} from "../decisions/decideCombatStep";
import {getCachedRoundPlan, getPlannerMode, setCachedRoundPlan} from "./plannerStore";
import {buildRoundPlan} from "./roundPlan";
import {calcBfsDistances} from "../botGrid";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {calcCityWinPoints} from "../../gameState/winPoints/calcCityWinPoints";
import {calcCityGarrisonStrength} from "../evaluation/armyStrength";
import {NEUTRAL_PLAYER_COLOR, NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {FINAL_ROUND} from "../../../configs/game";
import {FACTIONS} from "../../../constants/factions";
import {isPlayerFaction} from "../../factions/isPlayerFaction";

// Кап шагов песочницы (страховка от зацикливания): раунд длиннее фазы войны,
// поэтому запас больше, чем в strongMode
const ROLLOUT_STEPS_PER_PLAYER = 400;
const ROLLOUT_STALL_LIMIT = 8;

// Кандидаты: {label, options для buildRoundPlan}. economy_lean дополнительно
// замораживает покупку НОВЫХ отрядов на раунд (найм размера остается) —
// анализ проб показал, что проигравшие систематически перевооружаются
const CANDIDATES = [
  {label: 'economy', options: {maxGarrison: -1}},
  {label: 'economy_lean', options: {maxGarrison: -1, hireFreeze: true}},
  // Фокус стройки: усиленные доходные строения против усиленных военных
  // (getPlannerBuildFocusFactor в planConstructionChain)
  {label: 'economy_build', options: {maxGarrison: -1, buildFocus: 'economy'}},
  {label: 'military_build', options: {buildFocus: 'military'}},
  {label: 'cheap', options: {}},
  {label: 'aggressive', options: {maxGarrison: 8, maxDistance: 10}},
];

/**
 * Прогноз финального счета игрока по состоянию: очки + монеты по курсу.
 * Доход не экстраполируется: горизонт песочницы уже включает 1-2 начисления
 * дохода, а сравниваются планы ОДНОГО игрока между собой.
 */
const projectScore = (player) => {
  const coinsRate = isPlayerFaction(player, FACTIONS.ENLIGHTENED) ? 1 : 0.5;
  return (player.winPoints || 0) + (player.coins || 0) * coinsRate;
};

// Детерминированный ГПСЧ для общих случайных чисел (mulberry32)
const createSeededRandom = (seed) => {
  let a = seed >>> 0;
  return () => {
    a += 0x6D2B79F5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * Прогон песочницы из состояния state до конца следующего раунда.
 * Возвращает разрыв счета: свой прогноз минус максимум среди противников.
 *
 * randomSeed: общие случайные числа — ОДИН сид для всех кандидатов раунда,
 * чтобы разница исходов отражала разницу планов, а не удачу бросков костей.
 */
const rolloutScore = (state, currentPlayerId, startRound, randomSeed) => {
  const strategySnapshot = snapshotBotStrategies();
  const auxStores = globalThis.__strongModeAuxStrategyStores || [];
  const auxSnapshots = auxStores.map((auxStore) => auxStore.snapshot());
  let finalState = null;

  // Подмена генератора на время прогона: песочница полностью синхронна,
  // поэтому подмена не задевает ничего снаружи
  const originalRandom = Math.random;
  Math.random = createSeededRandom(randomSeed);

  try {
    // structuredClone заметно быстрее JSON-round-trip; состояние игры — плоские
    // данные (оно ходит по сети), поэтому клонируется без потерь
    const sandbox = createStore(rootReducer, structuredClone(state));
    sandbox.__isBotSandbox = true;
    resetMeleeHandlingTracking();

    const playersCount = Math.max(1, selectPlayers(state).length);
    const maxSteps = ROLLOUT_STEPS_PER_PLAYER * playersCount * 2;
    let lastFingerprint = '';
    let stallCounter = 0;

    for (let step = 0; step < maxSteps; step++) {
      const sandboxState = sandbox.getState();
      if (selectGameResults(sandboxState)) break;
      // Горизонт: текущий раунд + horizonRounds следующих. R+2 проверен на
      // 300 партиях: +4.8 п.п. против ~+7.9 у R+1 при двойной цене — не
      // окупается. R+0 (только текущий раунд, __plannerHorizonRounds=0) —
      // кандидат на срез стоимости вдвое, проверяется пробой
      const horizonRounds = globalThis.__plannerHorizonRounds ?? 1;
      if (selectRound(sandboxState) > Math.min(startRound + horizonRounds, FINAL_ROUND)) break;
      const sandboxPlayer = selectCurrentPlayer(sandboxState);
      if (!sandboxPlayer) break;

      const fingerprint = `${selectRound(sandboxState)}|${sandboxPlayer.id}`;
      if (fingerprint === lastFingerprint) {
        stallCounter += 1;
      } else {
        stallCounter = 0;
        lastFingerprint = fingerprint;
      }

      if (!sandboxPlayer.isBot || stallCounter > ROLLOUT_STALL_LIMIT * 40) {
        // Живые игроки в песочнице пропускают ход; безнадежное застревание —
        // принудительное завершение (стрельба/бой могут требовать много шагов
        // одного игрока, поэтому порог намеренно высокий)
        handleCompleteTurn(sandbox, true, false, null);
        continue;
      }

      executeBotStep(sandbox, {});
    }

    finalState = sandbox.getState();
  } catch (error) {
    console.error('[Bot] Planner rollout sandbox failed', error);
  } finally {
    Math.random = originalRandom;
    restoreBotStrategies(strategySnapshot);
    auxStores.forEach((auxStore, index) => auxStore.restore(auxSnapshots[index]));
    resetMeleeHandlingTracking();
    (globalThis.__strongModeSandboxResets || []).forEach((reset) => reset());
  }

  if (!finalState) {
    return -Infinity;
  }
  const players = selectPlayers(finalState);
  const me = players.find((player) => player.id === currentPlayerId);
  if (!me) {
    return -Infinity;
  }
  let bestOpponent = 0;
  players.forEach((player) => {
    if (player.id !== currentPlayerId) {
      bestOpponent = Math.max(bestOpponent, projectScore(player));
    }
  });
  return projectScore(me) - bestOpponent;
};

/**
 * Кандидаты фокус-атак: до limit лучших целей для концентрации ВСЕЙ силы на
 * одном городе (планы cheap/aggressive размазывают группы по всем целям
 * сразу). Ранжирование: качание захвата против гарнизона.
 */
const pickFocusCityKeys = (deps, limit = 2) => {
  const {currentPlayer, units, players, citiesCache, hexes, gridRenderer} = deps;
  const myUnits = units.filter(
    (unit) => unit.ownerId === currentPlayer.id && unit.size >= 3
  );
  if (myUnits.length === 0) {
    return [];
  }
  const myArmy = myUnits.reduce((sum, unit) => sum + unit.size, 0);
  const distances = calcBfsDistances(myUnits.map((unit) => unit.hexKey), hexes, gridRenderer);
  const scored = [];
  Object.entries(citiesCache || {}).forEach(([color, cities]) => {
    if (color === currentPlayer.color) {
      return;
    }
    const owner = players.find((player) => player.color === color);
    const ownerId = owner ? owner.id : NEUTRAL_PLAYER_ID;
    const isNeutral = color === NEUTRAL_PLAYER_COLOR;
    (cities || []).forEach((city) => {
      const quarters = getAllQuartersOfCity(city);
      if (!quarters.some((quarterHexKey) => (distances.get(quarterHexKey) ?? Infinity) <= 10)) {
        return;
      }
      const garrison = calcCityGarrisonStrength(city, ownerId, units, gridRenderer);
      // Против гарнизона сильнее 80% своей армии фокус не соберется
      if (garrison > myArmy * 0.8) {
        return;
      }
      const swing = calcCityWinPoints(city) * (isNeutral ? 1 : 2);
      scored.push({key: city.centralQuarter, score: swing / (1 + garrison * 0.3)});
    });
  });
  return scored.sort((a, b) => b.score - a.score).slice(0, limit).map((entry) => entry.key);
};

/**
 * План раунда через роллауты (с кэшем на раунд). Вызывается только из
 * РЕАЛЬНОЙ игры (не из песочницы — там план читается из кэша).
 */
export const getOrSelectPlanByRollout = (deps) => {
  const {currentPlayer, round, state} = deps;
  const cached = getCachedRoundPlan(currentPlayer.id, round);
  if (cached) {
    return cached;
  }

  // Без отрядов сравнивать нечего — сразу экономический (пустой) план
  const hasUnits = selectUnits(state).some((unit) => unit.ownerId === currentPlayer.id);
  const focusCandidates = pickFocusCityKeys(deps).map((focusCityKey, index) => ({
    label: `focus_${index}`,
    options: {focusCityKey, maxGarrison: 12, maxDistance: 10},
  }));
  const allCandidates = [...CANDIDATES, ...focusCandidates];
  // Последние раунды: кандидат тотальной агрессии — терять уже нечего, а
  // захваты дают самые крупные качания; роллаут сам решит, окупается ли all-in
  if (round >= FINAL_ROUND - 2) {
    allCandidates.push({label: 'endgame_allin', options: {maxGarrison: 99, maxDistance: 12}});
  }
  // BFS-дистанции отрядов одинаковы для всех кандидатов — один кэш на раунд
  const sharedUnitDistances = new Map();
  const candidatePlans = allCandidates.map((candidate) => ({
    label: candidate.label,
    plan: buildRoundPlan(deps, {...candidate.options, sharedUnitDistances}),
  }));

  // Дедупликация: планы с одинаковыми назначениями не гоняются повторно
  const planKey = (plan) =>
    `${plan.hireFreeze ? 'F|' : ''}${plan.buildFocus || ''}|`
    + JSON.stringify([...plan.assaultTargets.entries()].sort());
  const uniquePlans = [];
  const seen = new Set();
  candidatePlans.forEach((candidate) => {
    const key = planKey(candidate.plan);
    if (!seen.has(key)) {
      seen.add(key);
      uniquePlans.push(candidate);
    }
  });

  let chosen = uniquePlans[0];
  if (hasUnits && uniquePlans.length > 1) {
    // Общие случайные числа: один сид на всех кандидатов раунда — кости
    // выпадают одинаково, сравнение отражает только разницу планов
    const sharedSeed = (Math.random() * 0xFFFFFFFF) >>> 0;
    let bestScore = -Infinity;
    uniquePlans.forEach((candidate) => {
      // Кандидат управляет ботом внутри песочницы через модульный кэш
      setCachedRoundPlan(currentPlayer.id, round, candidate.plan);
      const score = rolloutScore(state, currentPlayer.id, round, sharedSeed);
      if (score > bestScore) {
        bestScore = score;
        chosen = candidate;
      }
    });
  }

  setCachedRoundPlan(currentPlayer.id, round, chosen.plan);
  return chosen.plan;
};

/**
 * Единая точка получения плана раунда для decide*-шагов: роллауты при
 * включенном __plannerRollouts (кроме песочницы — там план приходит из кэша
 * или строится дефолтно, вложенных роллаутов нет).
 */
export const ensureRoundPlan = (deps, isSandbox) => {
  // Дуэльные поля: 160 партий 2p дали −3.8 ± 11 — пользы нет, риск минуса
  // реален (фокус-атаки без третьих сторон наказываются). Планировщик
  // отключается: пустой план с disabled=true возвращает поведение движка
  // без планировщика (и роллауты не запускаются вовсе)
  if (selectPlayers(deps.state).length <= 2) {
    const cachedDisabled = getCachedRoundPlan(deps.currentPlayer.id, deps.round);
    if (cachedDisabled) {
      return cachedDisabled;
    }
    const disabledPlan = {
      assaultTargets: new Map(), plannedCities: [],
      hireFreeze: false, buildFocus: null, disabled: true,
    };
    setCachedRoundPlan(deps.currentPlayer.id, deps.round, disabledPlan);
    return disabledPlan;
  }
  if (getPlannerMode(deps.state, deps.currentPlayer.id).rollouts && !isSandbox) {
    return getOrSelectPlanByRollout(deps);
  }
  const cached = getCachedRoundPlan(deps.currentPlayer.id, deps.round);
  if (cached) {
    return cached;
  }
  const plan = buildRoundPlan(deps);
  setCachedRoundPlan(deps.currentPlayer.id, deps.round, plan);
  return plan;
};
